"use client";

import React from "react";
import Image from "next/image";
import Fuse from "fuse.js";
import { useSetAtom } from "jotai";
import { X, Search } from "lucide-react";
import { useAction } from "next-safe-action/hooks";

import Loader from "@/components/loader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { examPreferences, saveUserExamPreferences } from "@/fetchers/auth";

import { authScreenAtom, showLoginAtom } from "@/store/auth";

import useMoengageEventTracker from "@/hooks/use-moengage";

import EVENT_NAME from "@/hooks/use-moengage/event-name.json";
import EVENT_ATTRIBUTE from "@/hooks/use-moengage/event-attribute.json";

function AuthSceneUserGoal({ onClose }) {
  const trackMoengageEvent = useMoengageEventTracker();

  const setShowLogin = useSetAtom(showLoginAtom);
  const setAuthScreen = useSetAtom(authScreenAtom);

  const [searchInput, setSearchInput] = React.useState("");

  const { result: examPreferenceResult, execute: fetchExamPreferences } =
    useAction(examPreferences);
  const { isExecuting, executeAsync: setUserGoal } = useAction(
    saveUserExamPreferences
  );

  React.useEffect(() => {
    fetchExamPreferences();
  }, [fetchExamPreferences]);

  const { popularGoals, otherGoals } = React.useMemo(() => {
    let filteredGoals = examPreferenceResult?.data || [];

    const searchList = new Fuse(filteredGoals, {
      keys: ["name"],
    });

    if (searchInput?.trim()?.length > 0) {
      // Track search event only if input is more than 3 characters
      trackMoengageEvent(EVENT_NAME.GOAL_SEARCHED, {
        [EVENT_ATTRIBUTE.GOAL_SEARCHED]: searchInput,
      });

      // Perform search using Fuse.js
      // This will return an array of objects that match the search input
      filteredGoals = searchList.search(searchInput).map((item) => item.item);
    }

    // Return popular and other goals based on sortOrder
    return {
      popularGoals: filteredGoals.filter((item) => item.sortOrder === 1),
      otherGoals: filteredGoals.filter((item) => item.sortOrder !== 1),
    };
  }, [searchInput, examPreferenceResult]);

  const handleGoalSearch = (event) => setSearchInput(event.currentTarget.value);

  const handleSaveGoal = (goal, type) => async () => {
    if (!goal?.id) return;

    trackMoengageEvent(EVENT_NAME.USER_EXAM_SELECTED, {
      [EVENT_ATTRIBUTE.GOAL_SELECTED]: goal.name,
      [EVENT_ATTRIBUTE.GOAL_TYPE]: type,
    });

    await setUserGoal({ categoryId: goal.id });

    setShowLogin(false);
    setAuthScreen("AUTHENTICATION");
  };

  return (
    <div className="relative flex flex-col h-full">
      {isExecuting && <Loader />}

      <div className="flex items-center gap-x-2 -mx-7">
        <Button
          variant="link"
          size="icon"
          className="size-6"
          type="button"
          onClick={onClose}
        >
          <X size={18} />
        </Button>
        <span>Exam Preference</span>
      </div>

      <div className="flex flex-col my-4 flex-1">
        <label className="flex items-center shadow border border-grayscale-input px-2 rounded-lg">
          <Search
            className="size-4 text-grayscale-placeholder"
            strokeWidth={3}
          />
          <Input
            type="text"
            placeholder="Search Your Goal"
            className="border-none shadow-none"
            onChange={handleGoalSearch}
          />
        </label>

        <div className="flex-1 max-h-[calc(100vh_-_135px)] overflow-y-auto">
          {popularGoals.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-lg font-semibold mt-4">Popular Goals</h3>
              <ul className="space-y-2">
                {popularGoals.map((goal) => (
                  <li
                    key={goal.id}
                    className="flex items-center gap-x-2 rounded py-2 px-3 border border-grayscale-line cursor-pointer"
                    onClick={handleSaveGoal(goal, "popular")}
                  >
                    <Image
                      src={goal.image}
                      alt={goal.name}
                      width={16}
                      height={16}
                      className="size-4"
                    />
                    <span>{goal.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {otherGoals.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-lg font-semibold mt-4">Other Goals</h3>
              <ul className="space-y-2">
                {otherGoals.map((goal) => (
                  <li
                    key={goal.id}
                    className="flex items-center gap-x-2 rounded py-2 px-3 border border-grayscale-line cursor-pointer"
                    onClick={handleSaveGoal(goal, "other")}
                  >
                    <Image
                      src={goal.image}
                      alt={goal.name}
                      width={16}
                      height={16}
                      className="size-4"
                    />
                    <span>{goal.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AuthSceneUserGoal;
